import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { TicketPriority, TicketStatus, TicketQueues, TicketFilters } from "helpers/Enums";
import { Dictionary } from "lodash";
import { useIntl } from "react-intl";
import { LocalizationIds } from "helpers/Localizations";
import moment from "moment";
import { KeyValuePair } from "helpers";
import { appUserContext } from "helpers/services";


interface ITicketsContext {
    recordCount: number
    defaultSearch: ISearch
    search: ISearch
    tickets: ITicket[]
    isLoading: boolean
    anchorEl: any | null
    teamAnchorEl: any | null
    ticketQueues: ITicketQueue[]
    filtersByQueue: KeyValuePair<string, string[]>[]

    updateSearch: (newSearch: ISearch) => void
    doSearch: (newSearch: ISearch) => void,
    handleChangePage: (event: any, page: number) => void
    deleteTicket: (ticketId: string) => Promise<void>
    searchCustomers: (searchText: string, etag: number) => Promise<any>
    handleSubCategorySelection: (subCategoryId: string) => any
    handleTicketCategorySelection: (categoryId: string) => any
    updateAnchorEl: (value: any | null) => any
    updateTeamAnchorEl: (value: any | null) => any
}

export interface ITicket {
    ticketId: string
    number: string
    title: string
    categoryId: string
    ticketSubCategoryId: string
    ticketPriority: TicketPriority
    customerName: string
    ticketStatus: Dictionary<string>
    ordersCount: number
    orders: IOrder[]
    workOrdersCount: number
    workOrders: IWorkOrder[]
    assigneeUserName: string
}

interface IOrder {
    orderType: string
    orderNumber: string
    orderTitle: string
}

interface IWorkOrder {
    workOrderNumber: string
    articleId: string
    articleModel: string
    articleVersion: string
    articleDescription: string
}

export interface ISearch {
    ticketNumber: string
    status: TicketStatus[]
    ticketCategoryId: string
    ticketSubCategories: string[]
    customerId: number
    customerName: string
    orderNumber: string
    orderType: string
    workOrderNumber: string
    assigneeUserName: string
    personInTeam: string
    dateFrom?: moment.Moment | null
    dateTo?: moment.Moment | null
    countryCode: string
    title: string

    ticketQueue: TicketQueues

    pageNr: number
    pageSize: number
}

interface ITicketQueue {
    name: TicketQueues,
    label: string
}

export interface IState {
    search: ISearch
}

export let ticketsContext = React.createContext<ITicketsContext>({} as ITicketsContext);

let { Provider } = ticketsContext;

let TicketsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let { formatMessage } = useIntl();

    let history = useHistory<IState>();
    let location = useLocation<IState>();

    let { currentUser } = useContext(appUserContext);

    const defaultSearch: ISearch = {
        ticketNumber: '',
        customerId: 0,
        customerName: '',
        ticketCategoryId: '',
        ticketSubCategories: [],
        status: [],
        orderNumber: '',
        orderType: '',
        workOrderNumber: '',
        assigneeUserName: '',
        personInTeam: '',
        dateFrom: null,
        dateTo: null,
        countryCode: '',
        title: '',
        ticketQueue: currentUser.simecClaims.canViewDashboard ? TicketQueues.Dashboard : TicketQueues.Mine,
        pageNr: 0,
        pageSize: 20
    }

    let [isLoading, setLoading] = useState(false);
    let [tickets, setTickets] = useState<ITicket[]>([]);
    let [recordCount, setTotalRecords] = useState(0);
    let [search, setSearch] = useState<ISearch>(defaultSearch);

    let [anchorEl, setAnchorEl] = useState<any | null>(null);
    let [teamAnchorEl, setTeamAnchorEl] = useState<any | null>(null);

    const ticketQueues: ITicketQueue[] = [
        {
            name: TicketQueues.Dashboard,
            label: formatMessage({ id: LocalizationIds.Dashboard }),
        },
        {
            name: TicketQueues.Open,
            label: formatMessage({ id: LocalizationIds.Open }),
        },
        {
            name: TicketQueues.Unassigned,
            label: formatMessage({ id: LocalizationIds.Unassigned }),
        },
        {
            name: TicketQueues.Mine,
            label: formatMessage({ id: LocalizationIds.AssignedToMe }),
        },
        {
            name: TicketQueues.WaitingForSimec,
            label: formatMessage({ id: LocalizationIds.WaitingForSimec }),
        },
        {
            name: TicketQueues.WaitingForCustomer,
            label: formatMessage({ id: LocalizationIds.WaitingForCustomer }),
        },
        {
            name: TicketQueues.AdvancedSearch,
            label: formatMessage({ id: LocalizationIds.AdvancedSearch }),
        }
    ]

    const allFilters = [
        TicketFilters.Ticket,
        TicketFilters.Customer,
        TicketFilters.Machine,
        TicketFilters.Country,
        TicketFilters.Assignee,
        TicketFilters.Status,
        TicketFilters.Jobsite,
        TicketFilters.Category,
        TicketFilters.Title
    ]

    const filtersByQueue = [
        new KeyValuePair<string, string[]>(
            TicketQueues.Open,
            [...allFilters.filter(x => x !== TicketFilters.Status)]
        ),
        new KeyValuePair<string, string[]>(
            TicketQueues.Unassigned,
            [...allFilters.filter(x => x !== TicketFilters.Assignee)]
        ),
        new KeyValuePair<string, string[]>(
            TicketQueues.Mine,
            [...allFilters.filter(x => x !== TicketFilters.Assignee)]
        ),
        new KeyValuePair<string, string[]>(
            TicketQueues.WaitingForSimec,
            [...allFilters.filter(x => x !== TicketFilters.Status)]
        ),
        new KeyValuePair<string, string[]>(
            TicketQueues.WaitingForCustomer,
            [...allFilters.filter(x => x !== TicketFilters.Status)]
        ),
        new KeyValuePair<string, string[]>(TicketQueues.AdvancedSearch, allFilters)
    ]


    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: { search } }} />


    let processState = (state: IState) => {
        if (state.search) {
            let request: ISearch = {
                ...state.search,
                dateFrom: (state.search.dateFrom != null) ? moment(state.search.dateFrom).utc(true) : null,
                dateTo: (state.search.dateTo != null) ? moment(state.search.dateTo).utc(true) : null,
            };


            setSearch(request);

            if (state.search.ticketQueue === TicketQueues.Dashboard)
                return;

            searchTickets(request);
        }
    }

    let doSearch = (newSearch: ISearch) => {
        let request = {
            ...newSearch,
            dateFrom: (newSearch.dateFrom != null) ? moment(newSearch.dateFrom).toDate() : null,
            dateTo: (newSearch.dateTo != null) ? moment(newSearch.dateTo).toDate() : null,
            pageNr: newSearch.pageNr > 0 ? 0 : newSearch.pageNr
        } as ISearch

        history.push({
            pathname: location.pathname,
            state: { search: request }
        });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchTickets = async (searchRequest: ISearch) => {
        try {
            setLoading(true);

            let response = await axios.post('/api/Ticket/SearchTickets', searchRequest);
            setTickets(response.data.items);
            setTotalRecords(response.data.recordCount);

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }


    let deleteTicket = async (ticketId: string) => {
        if (window.confirm(formatMessage({ id: LocalizationIds.INFO_RemoveTicket }))) {
            try {
                await axios.delete(`/api/Ticket/DeleteTicket/${ticketId}`);
                setTickets(tickets.filter(ticket => ticket.ticketId !== ticketId));
                enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_TicketRemoved }), { variant: 'success' });
            } catch (err) { }
        }
    }

    let searchCustomers = async (searchText: string, etag: number) => {
        if (!searchText)
            return;

        try {
            const res = await axios.post('/api/Order/CustomersQuickSearch', { searchText, etag });
            return res.data;
        } catch { }
    }


    let handleSubCategorySelection = (subCategoryId: string) => {
        let subcategories = [...search.ticketSubCategories];
        let subcatIx = search.ticketSubCategories.findIndex(f => f === subCategoryId);
        if (subcatIx === -1) {
            subcategories.push(subCategoryId);
        } else {
            subcategories.splice(subcatIx, 1);
        }

        let request = {
            ...search,
            ticketSubCategories: subcategories || []
        }

        if (search.ticketCategoryId === '') {
            setAnchorEl(null);
        }

        updateSearch(request);
        doSearch(request);
    }

    let handleTicketCategorySelection = (categoryId: string) => {
        let newCategoryId = search.ticketCategoryId !== categoryId ? categoryId : '';

        let request = {
            ...search,
            ticketSubCategories: [],
            ticketCategoryId: newCategoryId
        }

        updateSearch(request);
        doSearch(request);
    }

    let updateAnchorEl = (value: any | null) => {
        setAnchorEl(value)
    }

    let updateTeamAnchorEl = (value: any | null) => {
        setTeamAnchorEl(value)
    }


    return (
        <Provider value={{
            recordCount,
            search,
            defaultSearch,
            tickets,
            isLoading,
            anchorEl,
            teamAnchorEl,
            ticketQueues,
            filtersByQueue,

            updateSearch,
            doSearch,
            handleChangePage,
            deleteTicket,
            searchCustomers,
            handleSubCategorySelection,
            handleTicketCategorySelection,
            updateAnchorEl,
            updateTeamAnchorEl
        }}>
            {children}
        </Provider>
    );
};

export default TicketsProvider;